import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getReportGroups, getEmbedConfig } from '../services/reportService';
import { logout, toggleTestMode } from '../features/auth/authSlice';
import { logoutUser } from '../services/authService';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import AiChatWidget from '../components/AiChatWidget';
import { startSession, endSession, endSessionBeacon, heartbeatSession, logPage } from '../services/usageService';

async function getActiveReportFilters(report) {
  if (!report) return [];

  const buildMap = (filters) => {
    const map = new Map();
    (filters || []).forEach(f => {
      const t = f?.target;
      const vals = f?.values;
      if (t?.table && t?.column && Array.isArray(vals) && vals.length) {
        const key = `${t.table}|${t.column}`;
        if (!map.has(key)) map.set(key, { table: t.table, column: t.column, values: new Set() });
        vals.forEach(v => {
          const rawVal = (v && typeof v === 'object') ? (v.value ?? '') : v;
          if (rawVal === '' || rawVal == null) return;
          map.get(key).values.add(String(rawVal));
        });
      }
    });
    return map;
  };

  const slicerMap = new Map();

  try {
    const pages = await report.getPages();
    const active = pages.find(p => p.isActive) || pages[0];

    if (active) {
      // Yalnızca görünür slicer'lar okunur.
      // page.getFilters() kaldırıldı: sayfa gezintisinden kalan stale cross-page filtreler
      // çelişen değerler üreterek yanlış DAX sorgularına yol açıyordu.
      // Gizli/synced slicer'lar (Hidden displayState) hariç tutulur: önceki sayfadan
      // inherit edilen hidden slicer state'i çelişen filtre yaratıyordu.
      const visuals = await active.getVisuals();
      const slicers = visuals.filter(v =>
        v.type === 'slicer' &&
        v.layout?.displayState?.mode !== models.VisualContainerDisplayMode.Hidden
      );
      await Promise.all(slicers.map(async (s) => {
        try {
          const st = await s.getSlicerState();
          buildMap(st?.filters).forEach((v, k) => slicerMap.set(k, v));
        } catch (e) {
          console.warn(`Slicer state okunamadı (${s.name}):`, e);
        }
      }));
    }
  } catch (e) {
    // Sessiz yutma yasak: boş filtre listesinin nedeni console'da görünür olmalı
    console.warn('Rapor filtreleri okunamadı — filtresiz devam ediliyor:', e);
  }

  return Array.from(slicerMap.values()).map(({ table, column, values }) => ({
    table,
    column,
    values: Array.from(values)
  }));
}

// Soru anında çağrılır: art arda iki okuma aynı sonucu verene kadar okur (maks. 4 deneme).
// Kullanıcı slicer değiştirip hemen soru sorarsa cascade güncellemesi sürüyor olabilir;
// kararlı (iki kez doğrulanmış) state göndererek geçici/yarım filtre durumlarını eler.
async function getStableReportFilters(report) {
  if (!report) return [];

  const fingerprint = (filters) =>
    JSON.stringify(
      filters.map(f => `${f.table}|${f.column}|${[...f.values].sort()}`).sort()
    );

  let prev = null;
  let last = [];
  for (let i = 0; i < 4; i++) {
    if (i > 0) await new Promise(r => setTimeout(r, 300));
    last = await getActiveReportFilters(report);
    const fp = fingerprint(last);
    if (fp === prev) return last;
    prev = fp;
  }
  // 4 okuma sonunda hâlâ değişiyorsa son değeri kabul et
  return last;
}

// Sekme görünür ve rapor açıkken 60 sn'de bir oturum durumu sunucuya bildirilir.
// Tarayıcı penceresi kapatıldığında hiçbir istemci event'i sunucuya ulaşamıyor;
// oturum bu sayede son heartbeat anıyla kapanmış olarak kalır.
const HEARTBEAT_INTERVAL_MS = 60000;

const ReportsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user, isAdmin, canAccessTest, isTestMode,
    primaryColor, secondaryColor, logoUrl, portalTitle,
    textColor, fontBold, fontItalic, fontSize,
  } = useSelector((state) => state.auth);

  const [reportGroups, setReportGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [embedConfig, setEmbedConfig] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);

  const embedRef = useRef(null);
  const sessionIdRef = useRef(null);
  const sessionStartRef = useRef(null);
  const currentPageRef = useRef(null);
  const pageStartRef = useRef(null);
  // Sekmeye geri dönüldüğünde oturumu yeniden açabilmek için; olay dinleyicileri
  // state'in güncel değerini göremediğinden ref üzerinden okunur.
  const selectedReportRef = useRef(null);
  const lastHeartbeatRef = useRef(0);

  const brand = {
    primary: primaryColor || '#1a1a2e',
    secondary: secondaryColor || '#2e75b6',
    title: portalTitle || 'Pharma BI Portal',
    logo: logoUrl || null,
    text: textColor || '#ffffff',
    bold: fontBold || false,
    italic: fontItalic || false,
    fontSize: fontSize || 16,
  };

  // Başlık yazı stili — header ve sidebar için ortak
  const brandTextStyle = {
    color: brand.text,
    fontWeight: brand.bold ? '700' : '400',
    fontStyle: brand.italic ? 'italic' : 'normal',
    fontSize: `${brand.fontSize}px`,
  };

  // Sayfa başlığı
  useEffect(() => {
    document.title = brand.title;
  }, [brand.title]);

  // Rapor gruplarını yükle
  useEffect(() => {
    fetchGroups();
  }, [isTestMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Oturumun o anki durumu. Hem kapanış hem heartbeat aynı gövdeyi gönderir;
  // aradaki tek fark `endReason` alanının dolu olup olmamasıdır.
  // Yalnızca ref okur, bu yüzden bağımlılık dizisi gerçekten boştur.
  const buildSessionPayload = useCallback(() => {
    if (!sessionIdRef.current || !sessionStartRef.current) return null;

    const now = Date.now();
    const payload = {
      sessionId: sessionIdRef.current,
      durationSeconds: Math.max(0, Math.floor((now - sessionStartRef.current) / 1000)),
    };

    if (currentPageRef.current && pageStartRef.current) {
      payload.lastPageName = currentPageRef.current;
      payload.lastPageDurationSeconds = Math.max(0, Math.floor((now - pageStartRef.current) / 1000));
    }

    return payload;
  }, []);

  // Aktif oturumu kapatan TEK giriş noktası.
  // Rapor değişimi, çıkış, sayfadan ayrılma, ortam değişimi ve sekme gizlenmesi —
  // hepsi buradan geçer. Son sayfanın logu oturum kapanışıyla aynı istekte gider;
  // aksi halde kullanıcı hiç sayfa değiştirmeden çıktığında sayfa logu hiç oluşmuyordu.
  const closeSession = useCallback((endReason, { useBeacon = false } = {}) => {
    const payload = buildSessionPayload();
    if (!payload) return Promise.resolve();
    payload.endReason = endReason;

    // Ref'ler istek gönderilmeden ÖNCE temizlenir: kapanış sırasında art arda
    // tetiklenen visibilitychange/pagehide aynı oturumu iki kez kapatmasın.
    sessionIdRef.current = null;
    sessionStartRef.current = null;
    currentPageRef.current = null;
    pageStartRef.current = null;

    if (useBeacon) {
      endSessionBeacon(payload);
      return Promise.resolve();
    }

    return endSession(payload).catch((e) =>
      console.warn('Oturum kaydı kapatılamadı:', e)
    );
  }, [buildSessionPayload]);

  // Aktif sayfayı hemen kaydet. Hem embed'in ilk render'ında hem de sekmeye geri
  // dönüp oturum yeniden açıldığında çağrılır; 2 sn'lik polling beklenmez.
  const captureActivePage = useCallback(async () => {
    if (!embedRef.current || !sessionIdRef.current || currentPageRef.current) return;

    try {
      const pages = await embedRef.current.getPages();
      const activePage = pages?.find(p => p.isActive);
      if (!activePage) return;

      currentPageRef.current = activePage.displayName;
      pageStartRef.current = Date.now();
    } catch (e) {
      console.warn('Aktif sayfa okunamadı — sayfa takibi polling ile başlayacak:', e);
    }
  }, []);

  // Oturumu açan TEK giriş noktası: rapor seçimi ve sekmeye geri dönüş.
  // Aktif sayfayı BURADA okumaz: rapor ilk seçildiğinde embed henüz oluşmamış
  // oluyor ve getPages() yanıt vermiyor. Açılış sayfasını embed'in 'rendered'
  // olayı yakalar; sekmeye geri dönüşte çağıran taraf captureActivePage'i ekler.
  const beginSession = useCallback(async (report) => {
    try {
      const sessionId = await startSession(report.id);
      if (!sessionId) return;

      sessionIdRef.current = sessionId;
      sessionStartRef.current = Date.now();
      lastHeartbeatRef.current = Date.now(); // ilk heartbeat bir aralık sonra
    } catch (e) {
      // Oturum açılamazsa rapor yine de görüntülenir, yalnızca loglanmaz.
      console.warn('Oturum başlatılamadı — bu görüntüleme loglanmayacak:', e);
    }
  }, []);

  // Sayfa gizlendiğinde oturumu kapat — kapanış senaryolarının TEK güvenilir sinyali.
  //
  // pagehide/beforeunload yalnızca sayfa yenilemede işe yarıyor: pencere X ile
  // kapatıldığında Chrome tüm süreci sonlandırıyor ve kuyruktaki beacon'ı ağa
  // çıkmadan iptal ediyor (keepalive fetch'in de aynı sınırı var). visibilitychange
  // ise sayfa hâlâ tam canlıyken tetiklendiği için sekme kapatma, pencere kapatma,
  // OS kill ve mobil arka plan durumlarının hepsini yakalıyor.
  //
  // Karşılığında oturum "görüntüleme aralığı" anlamına geliyor: sekmeye geri
  // dönüldüğünde yeni bir oturum açılır. Gizliyken rapora bakılmadığı için her
  // satırın süresi gerçek; raporlamada rapor+kullanıcı+gün bazında toplanır.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        closeSession('hidden', { useBeacon: true });
      } else if (selectedReportRef.current && !sessionIdRef.current) {
        // Geri dönüşte embed zaten render edilmiş durumda; 'rendered' olayı bir
        // daha tetiklenmeyeceği için aktif sayfayı burada okumak gerekiyor.
        beginSession(selectedReportRef.current).then(captureActivePage);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [closeSession, beginSession, captureActivePage]);

  // Yedek: bazı tarayıcılarda sayfa yenilemede visibilitychange tetiklenmiyor.
  // Hangisi önce çalışırsa oturumu kapatır, diğeri ref'ler boş olduğu için no-op.
  useEffect(() => {
    const handleUnload = () => closeSession('unload', { useBeacon: true });

    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [closeSession]);

  // SPA içinde bu sayfadan ayrılma (unmount) — sayfa kapanmadığı için normal istek yeterli.
  useEffect(() => {
    return () => { closeSession('navigate'); };
  }, [closeSession]);

  // Aktif sayfa polling (2 sn)
  useEffect(() => {
    const interval = setInterval(async () => {
      const sessionId = sessionIdRef.current;
      if (!embedRef.current || !sessionId) return;

      try {
        const pages = await embedRef.current.getPages();

        // getPages() beklenirken oturum kapanmış olabilir (rapor değişimi, çıkış).
        // Doğrulanmazsa önceki raporun sayfa adı yeni oturuma yazılıyor.
        if (sessionIdRef.current !== sessionId) return;

        const activePage = pages?.find(p => p.isActive);
        if (!activePage) return;

        const activePageName = activePage.displayName;

        if (currentPageRef.current && currentPageRef.current !== activePageName) {
          const duration = Math.max(0, Math.floor((Date.now() - pageStartRef.current) / 1000));
          logPage(sessionId, currentPageRef.current, duration)
            .catch((e) => console.warn('Sayfa logu gönderilemedi:', e));
          currentPageRef.current = activePageName;
          pageStartRef.current = Date.now();
        } else if (!currentPageRef.current) {
          currentPageRef.current = activePageName;
          pageStartRef.current = Date.now();
        }

        // Heartbeat aynı interval'a biner — ayrı timer yok.
        if (Date.now() - lastHeartbeatRef.current >= HEARTBEAT_INTERVAL_MS) {
          lastHeartbeatRef.current = Date.now();
          const payload = buildSessionPayload();
          if (payload) {
            heartbeatSession(payload)
              .catch((e) => console.warn('Heartbeat gönderilemedi:', e));
          }
        }
      } catch (e) {
        console.warn('Aktif sayfa okunamadı:', e);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [buildSessionPayload]);

  const fetchGroups = async () => {
    // Ortam değişiminde seçili rapor sıfırlanıyor; oturum da kapatılmalı,
    // aksi halde prod/test toggle'ı her seferinde açık bir oturum bırakıyordu.
    await closeSession('envswitch');
    selectedReportRef.current = null;

    setLoading(true);
    setSelectedReport(null);
    setEmbedConfig(null);
    try {
      const environment = isTestMode ? 'test' : 'prod';
      const data = await getReportGroups(environment);
      setReportGroups(data);
      const expanded = {};
      data.forEach(g => expanded[g.id] = true);
      setExpandedGroups(expanded);
    } catch {
      setError('Raporlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await closeSession('logout');
    selectedReportRef.current = null;
    try { await logoutUser(); } catch {}
    dispatch(logout());
    navigate('/login');
  };

  const handleReportSelect = async (report) => {
    await closeSession('switch');

    selectedReportRef.current = report;
    setSelectedReport(report);
    setEmbedConfig(null);
    setEmbedLoading(true);

    try {
      const config = await getEmbedConfig(report.id);
      setEmbedConfig(config);
      // Beklenmez: oturum kaydı raporun görüntülenmesini geciktirmemeli.
      beginSession(report);
    } catch {
      setError('Rapor yüklenirken bir hata oluştu.');
    } finally {
      setEmbedLoading(false);
    }
  };

  const embedEventHandlers = useMemo(
    () => new Map([['rendered', captureActivePage]]),
    [captureActivePage]
  );

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ ...styles.header, backgroundColor: brand.primary }}>
        <div style={styles.headerLeft}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.menuButton}>☰</button>
          {brand.logo ? (
            <img src={brand.logo} alt="Logo" style={styles.logo} />
          ) : (
            <h2 style={{ ...styles.title, ...brandTextStyle }}>{brand.title}</h2>
          )}
          {isTestMode && <span style={styles.testBadge}>TEST MODU</span>}
        </div>
        <div style={styles.userInfo}>
          <span style={{ ...styles.userName, ...brandTextStyle }}>{user?.fullName}</span>
          {canAccessTest && (
            <div style={styles.toggleContainer}>
              <span style={{ ...styles.toggleLabel, ...brandTextStyle }}>Test Modu</span>
              <div
                style={{ ...styles.toggle, ...(isTestMode ? { ...styles.toggleActive, backgroundColor: brand.secondary } : {}) }}
                onClick={() => dispatch(toggleTestMode())}
              >
                <div style={{ ...styles.toggleKnob, ...(isTestMode ? styles.toggleKnobActive : {}) }} />
              </div>
            </div>
          )}
          {isAdmin && (
            <button onClick={() => navigate('/admin')} style={styles.navButton}>Admin Panel</button>
          )}
          <button onClick={handleLogout} style={styles.logoutButton}>Çıkış Yap</button>
        </div>
      </div>

      <div style={styles.body}>
        {/* Sidebar */}
        {sidebarOpen && (
          <div style={{ ...styles.sidebar, backgroundColor: brand.primary + 'ee' }}>
            {loading && <p style={styles.sidebarInfo}>Yükleniyor...</p>}
            {error && <p style={styles.sidebarError}>{error}</p>}
            {!loading && reportGroups.length === 0 && (
              <p style={styles.sidebarInfo}>Rapor bulunamadı.</p>
            )}
            {reportGroups.map(group => (
              <div key={group.id}>
                <div style={styles.groupHeader} onClick={() => toggleGroup(group.id)}>
                  <span style={styles.groupIcon}>{expandedGroups[group.id] ? '▼' : '▶'}</span>
                  <span style={{
                    ...styles.groupName,
                    color: brand.text,
                    fontWeight: brand.bold ? '700' : '600',
                    fontStyle: brand.italic ? 'italic' : 'normal',
                    fontSize: `${brand.fontSize}px`,
                  }}>
                    {group.name}
                  </span>
                </div>
                {expandedGroups[group.id] && (
                  <div style={styles.reportList}>
                    {group.reports.map(report => (
                      <div
                        key={report.id}
                        style={{
                          ...styles.reportItem,
                          color: brand.text + 'cc', // biraz daha soluk — alt öğe hissi
                          ...(selectedReport?.id === report.id ? {
                            ...styles.reportItemActive,
                            borderLeft: `2px solid ${brand.secondary}`,
                            color: brand.text,
                          } : {})
                        }}
                        onClick={() => handleReportSelect(report)}
                      >
                        📊 {report.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Ana İçerik */}
        <div style={styles.content}>
          {selectedReport ? (
            <div style={{ height: '100%' }}>
              {embedLoading && <p style={styles.reportInfo}>Rapor yükleniyor...</p>}
              {embedConfig && (
                <PowerBIEmbed
                  embedConfig={{
                    type: 'report',
                    id: embedConfig.reportId,
                    embedUrl: embedConfig.embedUrl,
                    accessToken: embedConfig.embedToken,
                    tokenType: models.TokenType.Embed,
                    settings: {
                      panes: {
                        filters: { expanded: false, visible: false },
                        pageNavigation: { visible: false }
                      }
                    }
                  }}
                  eventHandlers={embedEventHandlers}
                  getEmbeddedComponent={(embeddedReport) => {
                    embedRef.current = embeddedReport;
                  }}
                  cssClassName="report-embed"
                />
              )}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <p>Sol menüden bir rapor seçin.</p>
            </div>
          )}
        </div>
      </div>

      {(() => {
        if (!selectedReport) return null;
        const activeGroup = reportGroups.find(g =>
          g.reports?.some(r => r.id === selectedReport?.id)
        );
        return activeGroup?.aiChatEnabled !== false ? (
          <AiChatWidget
            reportName={selectedReport?.name}
            reportId={selectedReport?.id}
            workspaceId={selectedReport?.workspaceId}
            primaryColor={brand.primary}
            getActiveFilters={async () => {
              try {
                return await getStableReportFilters(embedRef.current);
              } catch (e) {
                // Filtre okunamazsa soru filtre olmadan gönderilir; sessizce yutulmaz.
                console.warn('Aktif filtre okuma hatası — filtresiz devam ediliyor:', e);
                return [];
              }
            }}
          />
        ) : null;
      })()}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  menuButton: { backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' },
  logo: { height: '32px', objectFit: 'contain' },
  title: { margin: 0 }, // renk ve font brand'den geliyor
  testBadge: { backgroundColor: '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: {}, // renk ve font brand'den geliyor
  toggleContainer: { display: 'flex', alignItems: 'center', gap: '8px' },
  toggleLabel: {}, // renk ve font brand'den geliyor
  toggle: { width: '40px', height: '22px', backgroundColor: '#4b5563', borderRadius: '11px', cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s' },
  toggleActive: { backgroundColor: '#f59e0b' },
  toggleKnob: { width: '18px', height: '18px', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: '2px', transition: 'left 0.2s' },
  toggleKnobActive: { left: '20px' },
  navButton: { backgroundColor: 'transparent', border: '1px solid #94a3b8', color: '#94a3b8', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid white', color: 'white', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  sidebar: { width: '260px', overflowY: 'auto', padding: '16px 0' },
  sidebarInfo: { color: '#aaa', fontSize: '13px', padding: '0 16px' },
  sidebarError: { color: 'red', fontSize: '13px', padding: '0 16px' },
  groupHeader: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', cursor: 'pointer', userSelect: 'none' },
  groupIcon: { fontSize: '10px', color: '#94a3b8' },
  groupName: { flex: 1 }, // renk ve font brand'den geliyor
  reportList: { paddingLeft: '8px' },
  reportItem: { padding: '8px 16px 8px 32px', cursor: 'pointer', fontSize: '13px', borderLeft: '2px solid transparent' },
  reportItemActive: { backgroundColor: 'rgba(255,255,255,0.1)', borderLeft: '2px solid #3b82f6' },
  content: { flex: 1, overflow: 'hidden', padding: '0' },
  reportInfo: { color: '#666' },
  emptyState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#999', fontSize: '16px' },
};

export default ReportsPage;